import { z } from 'zod';

import { timeWindowSchema } from '../../utils/datetimes/types';
import { zodDate, zodDatetime } from '../../utils/zodHelpers';
import { serviceForListDisplaySchema } from '../api/services';
import { addressWithLatLngSchema } from './addresses';
import {
    nameAndCodeSchema,
    organizationBasicSchema,
    patientBasicSchema,
    prioritySchema,
    qhpSimpleSchema,
    serviceAreaBasicSchema,
} from './basicSchemas';
import { jsonSchema } from './jsonSchema';
import { contactSchema, disciplineSchema, documentWithLinkSchema } from './misc';
import { visitChangeSchema } from './visitChangeLog';

/**
 * TODO future refactor: the graph code shouldn't depend on any zod schemas;
 *  we should define these as typescript interfaces/types either inline or
 *  in the question and answers component.
 */
export interface Question {
    title: string;
    questionType: string;
    options: string[];
}

export interface Answer {
    value: z.infer<typeof jsonSchema>;
    question: Question;
}

export const statusSchema = nameAndCodeSchema.extend({
    color: z.string(),
});
export type Status = z.infer<typeof statusSchema>;

export const outcomeSchema = nameAndCodeSchema.extend({
    color: z.string(),
});
export type Outcome = z.infer<typeof outcomeSchema>;

export type Priority = z.infer<typeof prioritySchema>;
const visitServiceSchema = z.object({
    service: serviceForListDisplaySchema,
    is_completed: z.boolean().nullable(),
});

export enum Modality {
    Mobile = 'mobile',
    Virtual = 'virtual',
    Clinic = 'clinic',
}

const ZodModality = z.enum([Modality.Mobile, Modality.Virtual, Modality.Clinic]);

export const visitPositionForListSchema = z.object({
    position_uuid: z.string().uuid(),
    modality: ZodModality,
    visit_services: z.array(visitServiceSchema),
    start_offset_minutes: z.number().nullable(),
    duration_minutes: z.number(),
    duration_override_minutes: z.number().nullable(),
    qhp: qhpSimpleSchema.nullable(),
    state: statusSchema,
    outcome: outcomeSchema,
    outcome_notes: z.string(),
    mileage_mi: z.number().nullable(),
});
export type VisitPositionForList = z.infer<typeof visitPositionForListSchema>;

export const visitPositionForRouteOptimizerSchema = z.object({
    position_uuid: z.string().uuid(),
    modality: ZodModality,
    qhp: qhpSimpleSchema.nullable(),
});
export type VisitPositionForRouteOptimizer = z.infer<typeof visitPositionForRouteOptimizerSchema>;

export const visitForListSchema = z.object({
    uuid: z.string().uuid(),
    status: statusSchema,
    outcome: outcomeSchema,
    date: zodDate().nullable(),
    datetime: zodDatetime().nullable(),
    earliest_start_datetime: zodDatetime().nullable(),
    latest_end_datetime: zodDatetime().nullable(),
    tentative_date: z.optional(zodDate().nullable()),
    disciplines: z.optional(z.array(disciplineSchema).nullable()),
    duration_minutes: z.number(),
    timezone: z.string(),
    patient: patientBasicSchema,
    address_dict: addressWithLatLngSchema,
    org: organizationBasicSchema,
    coordinates: z
        .object({
            lat: z.string(),
            lng: z.string(),
        })
        .nullable(),
    service_area: serviceAreaBasicSchema,
    visit_positions: z.array(visitPositionForListSchema),
    priority: prioritySchema.nullable(),
    computed_priority: prioritySchema,
    created_at: zodDatetime(),
});
export type VisitForList = z.infer<typeof visitForListSchema>;

export const visitForRouteOptimizerSchema = visitForListSchema
    .omit({
        earliest_start_datetime: true,
        latest_end_datetime: true,
    })
    .extend({
        visit_positions: z.array(visitPositionForRouteOptimizerSchema),
    });
export type VisitForRouteOptimizer = z.infer<typeof visitForRouteOptimizerSchema>;

export const locationSchema = z.object({
    lat: z.string(),
    lng: z.string(),
    timestamp: zodDatetime(),
});
export const travelPathSchema = z.array(locationSchema);

export const visitDetailsSchema = visitForListSchema.extend({
    parent_uuid: z.string().uuid().nullable(),
    visit_order_uuid: z.string().uuid().nullable(),
    documents: z.array(documentWithLinkSchema),
    provider: z
        .object({
            name: z.string(),
        })
        .nullable(),
    booking_notes: z.string().nullable(),
    internal_notes: z.string().nullable(),
    external_id: z.string().nullable(),
    review: z
        .object({
            rating: z.number(),
            comments: z.string().nullable(),
            nps: z.number().nullable(),
        })
        .nullable(),
    qhp_notes: z.string().nullable(),
    timeslot_preferences: z.array(timeWindowSchema),
    status_changes: z
        .array(
            z.object({
                status: statusSchema,
                created_at: zodDatetime(),
            }),
        )
        .nullable(),
    travel_path: travelPathSchema.nullable(),
    is_incomplete: z.boolean(),
    visit_order_notes: z.string(),
    contact: contactSchema,
    changelog: z.array(visitChangeSchema),
    duration_override_minutes: z.number().nullable(),
    permissions: z.object({
        view: z.object({
            patient: z.boolean(),
            visit_order_details_view: z.boolean(),
        }),
        comms: z.object({
            visit: z.object({
                view: z.boolean(),
            }),
            qhp: z.object({
                view: z.boolean(),
                send: z.boolean(),
            }),
            patient: z.object({
                view: z.boolean(),
                send: z.boolean(),
            }),
        }),
        edit: z.object({
            status: z.boolean(),
            outcome: z.boolean(),
            datetime: z.boolean(),
            address: z.boolean(),
            assign: z.boolean(),
            cancel: z.boolean(),
            any: z.boolean(),
            cannot_edit_because_has_outcome: z.boolean(),
            services: z.boolean(),
            distance_traveled: z.boolean(),
            priority: z.boolean(),
            internal_notes: z.boolean(),
            booking_notes: z.boolean(),
            reschedule_workflow: z.boolean(),
        }),
    }),
});
export type VisitDetails = z.infer<typeof visitDetailsSchema>;
